import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Award, DollarSign, Zap } from 'lucide-react';
import styles from './style/PostLists.module.css';
import commonStyles from './../../../styles/Common.module.css';
import DOMPurify from 'dompurify';

// ----------------------------------------------------------------------------------
// ✨ CSS 클래스를 사용하여 재구현된 스켈레톤 UI 컴포넌트
// ----------------------------------------------------------------------------------
const SkeletonToolbar = () => (
    // 실제 toolbar 클래스와 skeleton-toolbar 클래스를 함께 사용
    <div className={`${styles.toolbar} ${styles['skeleton-toolbar']}`}></div>
);

const SkeletonPortfolioSection = ({ isMobile = false }) => (
    // isMobile에 따라 mobilePortfolioSection 또는 sidebar 클래스와 skeleton-portfolio-section을 사용
    <div className={`${styles['skeleton-portfolio-section']} ${isMobile ? styles.mobilePortfolioSection : styles.sidebar} ${isMobile ? styles.mobile : styles.sidebar}`}>
        
        {/* 섹션 제목 뼈대 */}
        <div className={styles['skeleton-title-line']}></div>
        
        {/* 카테고리 버튼 뼈대 컨테이너: 실제 버튼 컨테이너 스타일 재활용 */}
        <div className={styles.categoryButtonsContainer} style={{ justifyContent: 'flex-start', flexWrap: 'nowrap', overflow: 'hidden' }}>
            {/* 카테고리 버튼 뼈대 */}
            <div className={styles['skeleton-category-line']}></div>
            <div className={styles['skeleton-category-line']}></div>
            <div className={styles['skeleton-category-line']}></div>
        </div>
        
        {/* 포트폴리오 아이템 목록 뼈대 */}
        <div className={isMobile ? styles.portfolioCardsContainer : styles.sidebarCategory} style={isMobile ? {} : {borderBottom: 'none'}}>
          {[...Array(isMobile ? 3 : 5)].map((_, i) => (
              <div key={i} className={styles['skeleton-portfolio-item']}></div>
          ))}
        </div>
    </div>
);

const SkeletonPostFeed = ({ count }) => (
    <div className={styles.postFeed}>
        {[...Array(count)].map((_, index) => (
            // 실제 postItem의 박스 스타일을 유지하기 위해 skeleton-post-item 클래스 사용
            <article key={index} className={styles['skeleton-post-item']}>
                {/* 작성자 정보 뼈대 */}
                <div className={styles['skeleton-author-info']}>
                    <div className={styles['skeleton-circle']}></div>
                    <div className={styles['skeleton-line']}></div>
                </div>
                {/* 포스트 내용 뼈대 (텍스트 + 이미지) */}
                <div className={styles['skeleton-content-row']}>
                    <div className={styles['skeleton-text-group']}>
                        <div className={styles['skeleton-title-big']}></div>
                        <div className={styles['skeleton-preview-line']}></div>
                        <div className={styles['skeleton-preview-line-short']}></div>
                    </div>
                    {/* 이미지 뼈대 */}
                    <div className={styles['skeleton-image']}></div>
                </div>
            </article>
        ))}
    </div>
);
// ----------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------


const API_URL = process.env.REACT_APP_API_BASE_URL || "https://apmvd2shwh.execute-api.ap-northeast-2.amazonaws.com/prod";
const PAGE_SIZE = 10;
const DEFAULT_PROFILE_URL = process.env.REACT_APP_DEFAULT_PROFILE_IMAGE || "https://www.gravatar.com/avatar/?d=mp";

const sortCriteriaMap = {
  return: "ret_1y",
  sharpe: "sharpe_1y",
  mdd: "mdd_1y",
  calmar: "calmar_1y",
  volatility: "vol_1y"
};

export default function PostsLists() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [filterByAuthorId, setFilterByAuthorId] = useState(null);
  const [posts, setPosts] = useState([]);
  const [topCategoryPortfolios, setTopCategoryPortfolios] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [nextToken, setNextToken] = useState(null);
  const [error, setError] = useState(null);
  
  const loadingRef = useRef(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  // useRef를 사용하여 portfolioCategories 배열이 리렌더링될 때마다 새로 생성되지 않도록 함
  const portfolioCategoriesRef = useRef([
    { id: 'return', label: t('postLists.categoryReturn'), icon: <DollarSign size={16} /> },
    { id: 'sharpe', label: t('postLists.categorySharpe'), icon: <Award size={16} /> },
    { id: 'volatility', label: t('postLists.categoryVolatility'), icon: <Zap size={16} /> },
  ]);

  const [selectedTopPortfolioCategory, setSelectedTopPortfolioCategory] = useState(portfolioCategoriesRef.current[0].id);
  const observer = useRef(null);
  const [filteredAuthorNickname, setFilteredAuthorNickname] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(inputValue);
    setFilterByAuthorId(null);
    setFilteredAuthorNickname(null);
    navigate('/posts');
  };

  const loadPosts = useCallback(async (reset = false, lastKey = null) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setError(null);
    
    if (reset && !initialLoading) {
      setIsSearching(true);
    }

    const params = new URLSearchParams({
      sortBy: 'latest',
      limit: PAGE_SIZE,
    });
    if (search) params.append('search', search);
    if (filterByAuthorId) params.append('userId', filterByAuthorId);
    if (!reset && lastKey) params.append('lastEvaluatedKey', lastKey);

    const url = `${API_URL}/posts?${params.toString()}`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Status ${res.status}: ${res.statusText}`);
      const data = await res.json();
      
      const items = data.items || [];
      if (!Array.isArray(items)) {
        throw new Error('Invalid data format: items is not an array');
      }
      
      setPosts(prev => reset ? items : [...prev, ...items]);
      setNextToken(data.nextToken || null);
      setHasMore(!!data.nextToken);
      
    } catch (err) {
      console.error('Error loading posts:', err.message, err.stack);
      setError(t('postLists.loadingFailed', { message: err.message }));
    } finally {
      loadingRef.current = false;
      if (initialLoading) setInitialLoading(false);
      setIsSearching(false);
    }
  }, [search, filterByAuthorId, initialLoading, t]);

  useEffect(() => {
    document.title = 'QuanPort-Insights';
  }, [t]);

  useEffect(() => {
    loadPosts(true, null);
  }, [loadPosts]);

  useEffect(() => {
    if (!initialLoading) {
      setNextToken(null);
      setHasMore(true);
      loadPosts(true, null);
    }
  }, [search, filterByAuthorId, initialLoading, loadPosts]);

  const fetchTopPortfolios = useCallback(async () => {
    const fetchPromises = portfolioCategoriesRef.current.map(category => {
      const backendSortBy = sortCriteriaMap[category.id];
      const direction = (category.id === 'mdd' || category.id === 'volatility') ? 'asc' : 'desc';
      
      let url = `${API_URL}/portfolios?limit=10&sortby=${backendSortBy}&direction=${direction}`;
      
      // 'mdd'를 포함한 모든 지표에 대해 0보다 큰 값만 요청하도록 min_value 조건 추가
      if (['return', 'sharpe', 'calmar', 'volatility', 'mdd'].includes(category.id)) {
        url += '&min_value=0';
      }
      
      return fetch(url).then(res => res.json()).then(data => ({ categoryId: category.id, data: data.portfolios || [] }));
    });

    try {
      const results = await Promise.all(fetchPromises);
      const allCategoryData = {};
      results.forEach(result => {
        allCategoryData[result.categoryId] = result.data;
      });
      setTopCategoryPortfolios(allCategoryData);
    } catch (err) {
      console.error("Error loading top portfolios:", err);
    }
  }, []);

  useEffect(() => {
    fetchTopPortfolios();
  }, [fetchTopPortfolios, i18n.language]);

  const throttle = (func, limit) => {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  const throttledLoadPosts = throttle(loadPosts, 500);

  const lastPostElementRef = useCallback((node) => {
    if (loadingRef.current || initialLoading || isSearching) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          throttledLoadPosts(false, nextToken);
        }
      },
      {
        rootMargin: '300px',
        threshold: 0.1,
      }
    );
    if (node) observer.current.observe(node);
  }, [hasMore, nextToken, initialLoading, isSearching, throttledLoadPosts]);

  const handlePortfolioClick = (authorId, nickname) => {
    if (filterByAuthorId === authorId) {
      setFilterByAuthorId(null);
      setFilteredAuthorNickname(null);
      navigate('/posts');
    } else {
      setFilterByAuthorId(authorId);
      setFilteredAuthorNickname(nickname); 
      navigate(`/posts?author=${authorId}`);
    }
    setSearch('');
    setInputValue('');
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const authorParam = queryParams.get('author');
    if (authorParam && authorParam !== filterByAuthorId) {
      setFilterByAuthorId(authorParam);
    } else if (!authorParam && filterByAuthorId) {
      setFilterByAuthorId(null);
      setFilteredAuthorNickname(null);
    }
  }, [location.search, filterByAuthorId]);

  const formatPortfolioValue = (portfolio, criteria) => {
    let value = portfolio[sortCriteriaMap[criteria]];
    if (criteria === 'return' || criteria === 'mdd') {
      return isNaN(value) ? "-" : `${(value * 100).toFixed(2)}%`;
    }
    return isNaN(value) ? "-" : value.toFixed(2);
  };
  
  const renderPortfolioSection = (title, isMobile = false) => (
    <div className={isMobile ? styles.mobilePortfolioSection : styles.sidebar}>
      <h3 className={isMobile ? styles.sectionTitle : styles.sidebarTitle}>{title}</h3>
      <div className={styles.categoryButtonsContainer}>
        {portfolioCategoriesRef.current.map(category => (
          <button
            key={category.id}
            className={`${styles.categoryButton} ${selectedTopPortfolioCategory === category.id ? styles.activeCategoryButton : ''}`}
            onClick={() => setSelectedTopPortfolioCategory(category.id)}
          >
            {category.label}
          </button>
        ))}
      </div>
      
      {selectedTopPortfolioCategory && (
        <div className={isMobile ? styles.portfolioCardsContainer : styles.sidebarCategory}>
          {!isMobile && (
            <h4 className={styles.categoryTitle}>
              {portfolioCategoriesRef.current.find(c => c.id === selectedTopPortfolioCategory).icon} 
              {portfolioCategoriesRef.current.find(c => c.id === selectedTopPortfolioCategory).label}
            </h4>
          )}
          {topCategoryPortfolios[selectedTopPortfolioCategory] && topCategoryPortfolios[selectedTopPortfolioCategory].length > 0 ? (
            topCategoryPortfolios[selectedTopPortfolioCategory].slice(0, isMobile ? 5 : 10).map(portfolio => (
              <div
                key={portfolio.portfolioId}
                className={isMobile ? styles.portfolioCard : styles.sidebarPortfolioItem}
                onClick={() => handlePortfolioClick(portfolio.userId || portfolio.authorId, portfolio.nickname)}
              >
                <p className={styles.portfolioName}>
                  {DOMPurify.sanitize(portfolio.nickname || t('postLists.unnamedPortfolio'))}
                </p>
                <p className={styles.portfolioMetric}>
                  <span className={styles.valueHighlight}>
                    {formatPortfolioValue(portfolio, selectedTopPortfolioCategory)}
                  </span>
                </p>
              </div>
            ))
          ) : (
            <p className={styles.emptyCategoryMessage}>{t('postLists.noPortfoliosInCategory')}</p>
          )}
        </div>
      )}
    </div>
  );

  // ----------------------------------------------------------------------------------
  // ✨ 초기 로딩 시 스켈레톤 UI를 렌더링하도록 수정된 부분 (CSS 클래스 기반)
  // ----------------------------------------------------------------------------------
  if (initialLoading && posts.length === 0) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.mainContent}>
          {/* 툴바 스켈레톤 */}
          <SkeletonToolbar />
          
          {/* 모바일 포트폴리오 섹션 스켈레톤 */}
          <SkeletonPortfolioSection isMobile={true} />
          
          {/* 포스트 피드 스켈레톤 */}
          <section className={styles.postList}>
            <div className={styles.postFeed}>
              <SkeletonPostFeed count={PAGE_SIZE} /> 
            </div>
          </section>
        </div>

        {/* 사이드바 포트폴리오 섹션 스켈레톤 */}
        <SkeletonPortfolioSection isMobile={false} isSidebar={true} />
      </div>
    );
  }
  // ----------------------------------------------------------------------------------


  return (
    <div className={styles.pageContainer}>
      <div className={styles.mainContent}>
        {error && (
            <div className={commonStyles.errorMessage}>
                <p>{error}</p>
            </div>
        )}
        <form onSubmit={handleSearch} className={styles.toolbar}>
          <input
            type="text"
            placeholder={t('postLists.searchPlaceholder')}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            className={styles.searchInput}
          />
          {isSearching && (
            <div className={commonStyles.smallRowLoadingContainer}>
              <div className={commonStyles.spinner}></div>
              <p></p>
            </div>
          )}
          {filterByAuthorId && (
            <div className={styles.activeFilter}>
              <span>{t('postLists.filteredByAuthor', { nickname: filteredAuthorNickname || '필터링된 작성자' })}</span>
              <button onClick={() => {
                setFilterByAuthorId(null);
                setFilteredAuthorNickname(null);
                setSearch('');
                setInputValue('');
                navigate('/posts');
              }} className={styles.clearFilterButton}>X</button>
            </div>
          )}
        </form>

        {renderPortfolioSection(t('postLists.topPortfoliosTitle'), true)}

        <section className={styles.postList}>
          {posts.length > 0 ? (
            <div className={styles.postFeed}>
              {posts.map((post, index) => (
                <article
                  key={post.postId}
                  ref={posts.length === index + 1 ? lastPostElementRef : null}
                  className={styles.postItem}
                  onClick={() => navigate(`/posts/${post.postId}`)}
                >
                  <div className={styles.authorInfoContainer}>
                    <img
                      src={DOMPurify.sanitize(post.authorProfileImageUrl || DEFAULT_PROFILE_URL)}
                      alt={DOMPurify.sanitize(t('postLists.profileAlt', { nickname: post.authorNickname }))}
                      className={styles.authorProfileImage}
                    />
                    <span className={styles.authorNickname}>
                      {DOMPurify.sanitize(post.authorNickname)}
                    </span>
                  </div>

                  <div className={styles.postContent}>
                    <div className={styles.textContent}>
                      <h2>
                        {DOMPurify.sanitize(post.title)}
                      </h2>
                      {post.previewTitle && (
                        <p className={styles.preview}>
                          {DOMPurify.sanitize(post.previewTitle.substring(0, 200))}...
                        </p>
                      )}
                      <div className={styles.meta}>
                        <span className={styles.likes}>❤️ {t('postLists.likes')} {post.likes || 0}</span>
                        <span className={styles.comments}>💬 {t('postLists.comments')} {post.commentCount || 0}</span>
                        <span className={styles.date}>🗓️ {t('postLists.date')} {new Date(post.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    {post.previewImageUrl && (
                      <div className={styles.previewImageWrapper}>
                        <img
                          src={DOMPurify.sanitize(post.previewImageUrl)}
                          alt={DOMPurify.sanitize(t('postLists.profileAlt', { nickname: post.authorNickname }))}
                          className={styles.previewImage}
                        />
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            !initialLoading && !isSearching && <p className={styles.emptyMessage}>{t('postLists.noPosts')}</p>
          )}
          {loadingRef.current && posts.length > 0 && (
            <div className={commonStyles.smallRowLoadingContainer}>
              <div className={commonStyles.spinner}></div>
              <p>{t('postLists.loadingMore')}</p>
            </div>
          )}
          {!hasMore && posts.length > 0 && <p className={styles.endMessage}>{t('postLists.endOfPosts')}</p>}
        </section>
      </div>

      {renderPortfolioSection(t('postLists.topPortfoliosByCategory'))}
    </div>
  );
}